// Foyer glue, not a port. Renders the two item kinds the ported engine
// structurally cannot: `template` and `url`.
//
// ## Why this exists
//
// `legacyScheduleFromTimeline`'s `playableOnLegacy` (src/server/services/
// legacySignageSchedule.ts) only ever lets `image`/`video` items cross onto
// the wire schedule the ported machine consumes - confirmed by direct read,
// not assumed. A `template`/`url` item is dropped before the ported engine
// ever sees it exists. Left unhandled, an interval built entirely from one
// of these kinds would show nothing through `SignageEngineSurface` where
// `SignageSurface.tsx` renders it natively (`TemplateSlide.tsx`, a
// sandboxed `<iframe>`) - a real feature gap, not a crash, but a gap this
// component closes.
//
// ## Why this reads the timeline directly instead of asking the ported engine
//
// The ported engine's own state (`selectCurrentAsset` etc.) can never name a
// `template`/`url` item - it was never told one exists. The only place that
// information still lives is Foyer's own resolved `SignageTimeline`, so this
// component re-derives "what's actually playing right now" independently,
// via the SAME pure resolver function (`playheadAt`) `SignageSurface.tsx`
// itself uses - not a second implementation of that logic, the real one.
//
// `SignageEngineSurface` is the router: it calls `playheadAt` once, and
// renders EITHER this component (for template/url) OR `DualLayerPlayback`
// (for image/video/nothing) - never both. This component only ever
// receives an item it is meant to render.
import TemplateSlide from "./TemplateSlide";
import { iframeSandbox } from "./SignageSurface";
import type { SignageItem, SignageTimeline } from "@shared/types/signage";

interface NativeFallbackLayerProps {
  item: SignageItem;
  timeline: SignageTimeline;
  now: () => Date;
}

export default function NativeFallbackLayer({
  item,
  timeline,
  now,
}: NativeFallbackLayerProps) {
  if (item.kind === "template") {
    const slide = (timeline.templates ?? []).find(
      (t) => t.instanceId === item.templateInstanceId
    );
    // The resolver already warned (`template_unavailable`); the wall stays
    // silent, matching SignageSurface.tsx's own stance for this exact case.
    if (!slide) return null;
    return (
      <TemplateSlide
        slide={slide}
        timezone={timeline.timezone ?? "UTC"}
        now={now}
        // Same keep-alive as SignageSurface: see `webFrameHost.ts`.
        keepAliveWeb
      />
    );
  }

  if (item.kind === "url") {
    // `webUrl`, not `assetUrl` - the two are deliberately separate fields on
    // a url item, matching `playbackSrc`'s own rule in SignageSurface.tsx
    // (not exported/reused directly: that function branches on every item
    // kind and pulls in cache-lookup helpers this fallback has no business
    // depending on for two lines of logic).
    const src = item.webUrl ?? "";
    return (
      <iframe
        title=""
        src={src}
        sandbox={iframeSandbox(src)}
        tabIndex={-1}
        scrolling="no"
        style={{
          position: "fixed",
          inset: 0,
          width: "100%",
          height: "100%",
          border: 0,
          pointerEvents: "none",
        }}
      />
    );
  }

  return null;
}
